/**
 * AlarmList — list of active + recently-cleared alarms with ack action.
 *
 * Differs from EventFeed in two ways:
 *   - alarms carry `active` / `acknowledged` / `acknowledgedBy` state
 *   - the row exposes "acknowledge" / "view source" actions
 *
 * The list groups visually by state: active-unacked at top, then
 * active-acked, then recently-cleared.
 */

import { format, formatDistanceToNow } from "date-fns";
import { AlertTriangle, CheckCircle2, Eye, Info, MapPin, Sparkles, XCircle } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import { ChartSkeleton } from "../ChartSkeleton";
import { EmptyState } from "@/lib/ui/empty-state";
import type { AlarmItem, WidgetState } from "../types";

export interface AlarmListProps {
  alarms: AlarmItem[];
  onAcknowledge?: (alarm: AlarmItem) => void;
  onView?: (alarm: AlarmItem) => void;
  /**
   * "Investigate with copilot" handler — when present, renders a small
   * Sparkles button alongside Acknowledge / View. Use to deep-link to
   * the copilot session pre-populated with the alarm's context.
   */
  onInvestigate?: (alarm: AlarmItem) => void;
  /**
   * A row's own "Ask the consultant" link (the Alarms page's AskCopilot, 2026-09-28), rendered with the row's
   * actions. The widget stays free of the app's copilot: the page decides what the link asks.
   */
  renderAsk?: (alarm: AlarmItem) => React.ReactNode;
  /**
   * Resolve WHERE an alarm fired (A2) — the bound Tier-2 entity + stage,
   * e.g. "Induction Furnace · Heat". When provided and it returns a label,
   * the row shows a location badge above the raw tag source. Optional so the
   * widget stays usable without the plant-bindings context.
   */
  resolveWhere?: (alarm: AlarmItem) => string | undefined;
  state?: WidgetState;
  className?: string;
}

const SEVERITY_META = {
  info: { Icon: Info, ring: "ring-info/40", text: "text-info" },
  success: { Icon: CheckCircle2, ring: "ring-success/40", text: "text-success" },
  warning: { Icon: AlertTriangle, ring: "ring-warning/40", text: "text-warning" },
  danger: { Icon: XCircle, ring: "ring-danger/40", text: "text-danger" },
} as const;

export function AlarmList({
  alarms,
  onAcknowledge,
  onView,
  onInvestigate,
  renderAsk,
  resolveWhere,
  state = { kind: "ok" },
  className,
}: AlarmListProps) {
  if (state.kind === "loading") {
    return <ChartSkeleton shape="alarms" className={className} />;
  }

  if (alarms.length === 0) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="All clear"
        description="No active alarms right now. We'll tell you if anything fires."
        className={className}
      />
    );
  }

  // Sort: active+unacked first, then active+acked, then cleared. Within each
  // group, newest first.
  const sorted = [...alarms].sort((a, b) => {
    const score = (x: AlarmItem) =>
      x.active && !x.acknowledged ? 0 : x.active && x.acknowledged ? 1 : 2;
    const ra = score(a);
    const rb = score(b);
    if (ra !== rb) return ra - rb;
    return String(b.t).localeCompare(String(a.t));
  });

  return (
    <ul className={cn("flex flex-col gap-2", className)}>
      {sorted.map((alarm) => {
        const meta = SEVERITY_META[alarm.severity];
        const Icon = meta.Icon;
        const where = resolveWhere?.(alarm);
        return (
          <li
            key={alarm.id}
            className={cn(
              "flex flex-col gap-3 rounded-lg border border-border bg-surface-elevated p-3 transition-colors md:flex-row md:items-start md:gap-4",
              alarm.active && !alarm.acknowledged && "ring-1",
              alarm.active && !alarm.acknowledged && meta.ring,
              !alarm.active && "opacity-70",
            )}
          >
            <div className={cn("flex size-8 shrink-0 items-center justify-center rounded-full bg-surface", meta.text)}>
              <Icon className="size-4" />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-on-surface">
                  {alarm.title}
                </p>
                {!alarm.active && <Badge variant="secondary">Cleared</Badge>}
                {alarm.active && alarm.acknowledged && (
                  <Badge variant="info">Acknowledged</Badge>
                )}
                {alarm.active && !alarm.acknowledged && (
                  <Badge variant="danger">Active</Badge>
                )}
              </div>
              {where && (
                <span className="inline-flex w-fit items-center gap-1 rounded-full bg-surface-muted px-2 py-0.5 text-[11px] font-medium text-on-surface">
                  <MapPin className="size-3 text-on-surface-muted" />
                  {where}
                </span>
              )}
              {alarm.source && (
                <p className="font-mono text-[11px] text-on-surface-muted">
                  {alarm.source}
                </p>
              )}
              {alarm.description && (
                <p className="text-sm text-on-surface-muted">
                  {alarm.description}
                </p>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-on-surface-muted">
                <time dateTime={alarm.t} title={format(new Date(alarm.t), "PPpp")}>
                  Fired {formatRelative(alarm.t)}
                </time>
                {alarm.acknowledgedBy && (
                  <span>
                    Ack'd by{" "}
                    <span className="text-on-surface">{alarm.acknowledgedBy.user}</span>{" "}
                    {formatRelative(alarm.acknowledgedBy.at)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2 md:flex-col">
              {alarm.active && !alarm.acknowledged && onAcknowledge && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAcknowledge(alarm)}
                >
                  <CheckCircle2 className="mr-1.5 size-3.5" />
                  Acknowledge
                </Button>
              )}
              {onInvestigate && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onInvestigate(alarm)}
                  title="Investigate with AI Casting Consultant"
                >
                  <Sparkles className="mr-1.5 size-3.5" />
                  Investigate
                </Button>
              )}
              {onView && (
                <Button size="sm" variant="ghost" onClick={() => onView(alarm)}>
                  <Eye className="mr-1.5 size-3.5" />
                  View
                </Button>
              )}
              {renderAsk?.(alarm)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}
